import { useCallback, useEffect, useMemo, useState, useRef } from "react"
import { useAsyncSelector } from "src/hooks/useAsyncSelector"
import { useRefresher } from "src/hooks/useRefresher"
import { selectedMyOrganizationSelector } from "src/recoil/selectors/organization/selectedMyOrganizationSelector"
import { useContactDetailDataRefresher } from "src/recoil/hooks/refresher/useContactDetailDataRefresher"
import { useFetcher } from "src/hooks/useFetcher"
import { CustomAlert } from "src/utils/CustomAlert"
import { useRequestData } from "src/recoil/hooks/useRequestData"
import { useResetter } from "./contact/create/useResetter"
import { ContactFilterValue } from "src/types/contact/ContactFilter"
import { myProfileSelectorFamily } from "src/recoil/selectors/organization/myProfile/myProfileSelectorFamily"
import { deleteContactOutbox } from "src/apis/contact/deleteContactOutbox"
import { updateFavoriteContactOutbox } from "src/apis/contact/updateFavoriteContactOutbox"
import { deleteReservedContact } from "src/apis/contact/deleteReservedContact"
import { unreadThreadCountRequestIdState } from "src/recoil/atoms/contact/unreadThreadCountRequestIdState"
import { contactReplyThreadsRequestIdState } from "src/recoil/atoms/contact/contactReplyThreadsRequestIdState"
import { hasUnreadThreadMessageRequestIdState } from "src/recoil/atoms/contact/hasUnreadThreadMessageRequestIdState"
import { useRecoilState, useRecoilValue, useResetRecoilState } from "recoil"
import { outboxContactFavoriteFlgsCacheState } from "src/recoil/atoms/contact/contactFavoriteFlgsCacheState"
import { wait } from "src/utils/wait"
import { outboxContactsConcatSelectorFamily } from "src/recoil/selectors/contact/outboxContactsConcatSelectorFamily"
import { outboxContactsRequestIdState } from "src/recoil/atoms/contact/outboxContactsRequestIdState"
import { outboxContactsConcatRequestIdState } from "src/recoil/atoms/contact/outboxContactsConcatRequestIdState"
import { GetInboxContactsSuccess } from "src/apis/contact/getInboxContacts"
import { requestDataSelector } from "src/recoil/selectors/auth/requestDataSelector"
import { organizationDetailSelectorFamily } from "src/recoil/selectors/organization/organizationDetailSelectorFamily"
import { organizationRefreshState } from "src/recoil/atoms/organization/organizationRefreshPermissionState"
import { organizationDetailRequestIdState } from "src/recoil/atoms/organization/organizationDetailRequestIdState"
import { useIncrementalUnreadCount } from "src/hooks/useIncrementalUnreadCount"
import { ContactListCacheService } from "src/services/cache/ContactListCacheService"

export const useOutboxData = () => {
  const [searchText, setSearchText] = useState("")
  const [confirmedSearchText, setConfirmedSearchText] = useState("")
  const [filters, setFilters] = useState<ContactFilterValue[]>([])
  const { value: selectedOrganization } = useAsyncSelector(selectedMyOrganizationSelector)
  const organizationId = useMemo(() => selectedOrganization?.id, [selectedOrganization])
  const { value: organization } = useAsyncSelector(organizationDetailSelectorFamily(organizationId))
  const { value: myProfile } = useAsyncSelector(myProfileSelectorFamily(organizationId))
  const myMemberId = useMemo(() => myProfile?.memberId, [myProfile])
  const request = useRecoilValue(requestDataSelector)
  const [nextTokens, setNextTokens] = useState<string[]>([])
  // 組織切り替え時のリフレッシュ用（前回の組織IDを保持）
  const prevOrganizationIdRef = useRef<string | undefined>(undefined)
  // 組織切り替えが発生したかのフラグ（リフレッシュ関数が定義された後に実行するため）
  const [needsOrganizationSwitchRefresh, setNeedsOrganizationSwitchRefresh] = useState(false)
  useEffect(() => {
    setNextTokens([])
    // 組織が変わった場合（初回のundefined→idは除く）
    if (prevOrganizationIdRef.current !== undefined && prevOrganizationIdRef.current !== organizationId) {
      console.log("[useOutboxData] 組織切り替えを検出、セレクタを再評価します", {
        from: prevOrganizationIdRef.current,
        to: organizationId,
      })
      // リフレッシュ関数が定義された後に実行するためフラグをセット
      setNeedsOrganizationSwitchRefresh(true)
    }
    prevOrganizationIdRef.current = organizationId
  }, [organizationId, setNextTokens])
  const setConfirmedSearchTextHelper = useCallback((text: string) => {
    setNextTokens([])
    setConfirmedSearchText(text)
  }, [])
  const params = useMemo(
    () => ({
      searchText: confirmedSearchText !== "" ? confirmedSearchText : undefined,
      filters: filters.length !== 0 ? filters : undefined,
      organizationId, // 組織切り替え時にセレクタを再評価させるため
    }),
    [confirmedSearchText, filters, organizationId]
  )
  const { value: getContacts, isLoading: isLoadingContacts } = useAsyncSelector(
    outboxContactsConcatSelectorFamily({ ...params, nextTokens })
  )

  const _refreshContacts = useRefresher(outboxContactsRequestIdState)
  const _refreshContactsConcat = useResetRecoilState(outboxContactsConcatRequestIdState)
  const _resetFavoriteFlgsCache = useResetRecoilState(outboxContactFavoriteFlgsCacheState)
  const refreshContactReplyThreads = useRefresher(contactReplyThreadsRequestIdState)
  const refreshUnreadThreadCount = useRefresher(unreadThreadCountRequestIdState)
  const refreshHasUnreadThreadMessage = useRefresher(hasUnreadThreadMessageRequestIdState)
  const refreshOrganizationDetail = useRefresher(organizationDetailRequestIdState)
  const needRefreshOrganization = useRecoilValue(organizationRefreshState)
  useEffect(() => {
    if (needRefreshOrganization) {
      refreshOrganizationDetail()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needRefreshOrganization])

  // 組織切り替え時にRecoilセレクタを強制的に再評価
  useEffect(() => {
    if (needsOrganizationSwitchRefresh) {
      console.log("[useOutboxData] 組織切り替え後のセレクタ再評価を実行")
      _refreshContacts()
      _refreshContactsConcat()
      setNeedsOrganizationSwitchRefresh(false)
    }
  }, [needsOrganizationSwitchRefresh, _refreshContacts, _refreshContactsConcat])

  const refreshContactsInternal = useCallback(() => {
    // Pull-to-Refresh時はキャッシュを明示的にクリア
    if (myMemberId) {
      ContactListCacheService.invalidateContainer(myMemberId, "outbox")
    }

    setNextTokens([])
    _refreshContacts()
    _resetFavoriteFlgsCache()
    _refreshContactsConcat()
    refreshContactReplyThreads()
    refreshUnreadThreadCount()
    refreshHasUnreadThreadMessage()
    setNextTokens([])
    refreshOrganizationDetail()
  }, [
    myMemberId,
    _refreshContacts,
    _refreshContactsConcat,
    _resetFavoriteFlgsCache,
    refreshContactReplyThreads,
    refreshUnreadThreadCount,
    refreshHasUnreadThreadMessage,
    refreshOrganizationDetail,
  ])

  const refreshFiltersContacts = useCallback(() => {
    setNextTokens([])
    _refreshContactsConcat()
  }, [_refreshContactsConcat])

  const setFiltersHelper = useCallback(
    (filters: ContactFilterValue[]) => {
      setFilters(filters)
      // フィルタ変更時は必ずnextTokensをリセット（ページング状態をクリア）
      // お気に入りフィルタも含めて、すべてのフィルタ変更で1ページ目から取得する
      refreshFiltersContacts()
    },
    [refreshFiltersContacts]
  )

  // 画面フォーカス時の自動更新を削除（AWSコスト削減のため）
  // ユーザーはPull-to-Refreshで手動更新できる

  const [favoriteFlgsCache, setFavoriteFlgsCache] = useRecoilState(outboxContactFavoriteFlgsCacheState)
  const [contacts, setContacts] = useState<GetInboxContactsSuccess>()
  useEffect(() => {
    if (getContacts) {
      if (filters.includes("favorite") && getContacts.contacts) {
        const newContacts = getContacts.contacts.filter((e) => e.id && favoriteFlgsCache?.[e.id] !== false)
        setContacts({
          contacts: newContacts,
          nextToken: getContacts.nextToken,
        })
      } else {
        setContacts(getContacts)
      }
    }
  }, [getContacts, filters, favoriteFlgsCache])

  const refreshContactDetailData = useContactDetailDataRefresher()
  const requestDataResult = useRequestData()

  const reservedContactIdSet = useMemo(
    () => new Set((contacts?.contacts || []).filter((item) => item.reservedDate != null).map((item) => item.id)),
    [contacts]
  )
  const isReservedContact = useCallback((contactId: string) => reservedContactIdSet.has(contactId), [reservedContactIdSet])

  const { fetch: execDeleteContact } = useFetcher(
    useCallback(
      async (contactId: string) => {
        if (!requestDataResult.isOk) {
          return
        }
        const { accessToken } = requestDataResult.content
        if (accessToken == null || myMemberId == null) {
          return
        }
        const isReserved = isReservedContact(contactId)
        const isConfirmed = await CustomAlert.confirm(
          "確認",
          "送信トレイからこの連絡を削除しますか？\n（送信の取り消しではありません。）\n\n削除後は復元できません。\n送信の取り消し・編集・返信や結果の確認などができなくなります。\n※送信予約も取り消されます。"
        )
        if (!isConfirmed) {
          return
        }
        const result = isReserved
          ? await deleteReservedContact({ accessToken, reservedContactId: contactId })
          : await deleteContactOutbox({ accessToken, myMemberId, contactId })

        if (result.isOk) {
          await CustomAlert.alert("完了", isReserved ? "送信予約を取り消しました。" : "連絡を削除しました。")
          await wait(2000)
          refreshContactsInternal()
        } else {
          await CustomAlert.alert("エラー", result.error.message)
        }
      },
      [requestDataResult, refreshContactsInternal, myMemberId, isReservedContact]
    )
  )

  const { fetch: execUpdateFavoriteContact } = useFetcher(
    useCallback(
      async ({ contactId, isFavorite }: { contactId: string; isFavorite: boolean }) => {
        if (!requestDataResult.isOk) {
          return
        }
        const { accountId, accessToken } = requestDataResult.content
        if (accountId == null || accessToken == null || myMemberId == null) {
          return
        }
        const result = await updateFavoriteContactOutbox({ accessToken, myMemberId, contactId, isFavorite })
        if (!result.isOk) {
          CustomAlert.alert("エラー", result.error.message)
          return
        }
        setFavoriteFlgsCache((prev) => ({ ...prev, [contactId]: isFavorite }))

        // キャッシュ内のお気に入り状態も更新
        ContactListCacheService.updateContactFavorite({
          teamMemberId: myMemberId,
          contactId,
          isFavorite,
        })
        // 連絡詳細キャッシュも無効化（古い状態が残らないように）
        ContactListCacheService.deleteDetailByContactId(contactId)
        if (!isFavorite && result.isOk && filters.includes("favorite") && contacts && contacts.contacts) {
          const newContacts = contacts.contacts.filter((e: { id: string }) => e.id && e.id !== contactId)
          setContacts({
            contacts: newContacts,
            nextToken: contacts.nextToken,
          })
        }
      },
      [requestDataResult, myMemberId, setFavoriteFlgsCache, filters, contacts]
    )
  )

  const resetCreateNewContactScreenData = useResetter()

  // 増分的な未読数取得を使用
  const { unreadPerContact, resetUnreadCounts, forceRefreshUnreadCounts } = useIncrementalUnreadCount({
    contacts,
    request: request || null,
  })

  // フィルタやリフレッシュ時に未読カウントをリセット
  useEffect(() => {
    resetUnreadCounts()
  }, [confirmedSearchText, filters, resetUnreadCounts])

  // Pull-to-Refresh時に使用（キャッシュをクリア + 未読数をリセット）
  const refreshContacts = useCallback(() => {
    // 未読数の状態をリセット（processedContactIdsもクリア）
    // これにより、連絡一覧が更新された後にuseEffectが自動的に未読数を再取得する
    resetUnreadCounts()
    refreshContactsInternal()
  }, [refreshContactsInternal, resetUnreadCounts])

  return {
    searchText,
    setSearchText: setSearchText,
    confirmedSearchText,
    setConfirmedSearchText: setConfirmedSearchTextHelper,
    filters,
    setFilters: setFiltersHelper,
    contacts,
    isLoadingContacts,
    refreshContacts,
    _refreshContacts,
    organizationId,
    refreshContactDetailData,
    execDeleteContact,
    unreadPerContact,
    execUpdateFavoriteContact,
    resetCreateNewContactScreenData,
    isReservedContact,
    favoriteFlgsCache,
    setNextTokens,
    organization,
  }
}
